import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import ConfirmDialog from './ConfirmDialog';

interface UseConfirmResult {
  /** Opens the dialog; resolves true when confirmed, false when cancelled. */
  confirm: (message: string) => Promise<boolean>;
  /** Must be rendered by the calling component. */
  confirmDialog: ReactElement;
}

export function useConfirm(): UseConfirmResult {
  const [message, setMessage] = useState<string | null>(null);
  const resolverRef = useRef<((result: boolean) => void) | null>(null);

  // Last non-empty message, kept so the text doesn't blank during the dialog close transition
  const lastMessageRef = useRef('');
  if (message !== null) {
    lastMessageRef.current = message;
  }

  // Resolve a pending promise on unmount so awaiting callers don't hang
  useEffect(() => {
    return () => {
      resolverRef.current?.(false);
      resolverRef.current = null;
    };
  }, []);

  const confirm = useCallback((text: string): Promise<boolean> => {
    // A new request supersedes a pending one
    resolverRef.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setMessage(text);
    });
  }, []);

  const settle = (result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setMessage(null);
  };

  const handleConfirm = () => {
    settle(true);
  };

  const handleCancel = () => {
    settle(false);
  };

  const confirmDialog = (
    <ConfirmDialog
      open={message !== null}
      message={lastMessageRef.current}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return {
    confirm,
    confirmDialog,
  };
}
